import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom';
import { Icon } from '@iconify/react';
import moment from 'moment-timezone';
import { getAssignments } from '../../../services/userService'; 
import { encodeData } from '../../../utils/encodeAndEncode';

function Assignments({ classroom_id, classroomName }) {
  const navigate = useNavigate();
  const userId = JSON.parse(localStorage.getItem('user'))?.id;
  const [assignments, setAssignments] = useState([]);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [loading, setLoading] = useState(false);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const userTimeZone = moment.tz.guess(); // Get user's local timezone

  useEffect(() => {
    const fetchAssignments = async () => {
      try {
        setLoading(true);
        const response = await getAssignments({ 
          class_id: classroom_id, 
          user_id: userId,
          search: search,
          page: page,
          limit: itemsPerPage
        });
        // console.log("Assignments Response Data:", response);
        if(response.success === true){
          setAssignments(response.data.assignments || []);
          setTotalPages(response.data.pagination?.totalPages || 1);
          setTotalRecords(response.data.pagination?.totalRecords || 0);
        }
      } catch (error) {
        console.error("Error fetching assignments:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchAssignments();
  }, [classroom_id, userId, search, page, itemsPerPage]);

  const handleSearch = (e) => {
    setSearch(e.target.value);
    setPage(1);
  };

  const clearFilters = () => {
    setSearch('');
    setPage(1);
  };

  const handlePageChange = (newPage) => {
    setPage(newPage);
  };

  const handleLimitChange = (e) => {
    setItemsPerPage(parseInt(e.target.value, 10));
    setPage(1);
  };

  const getStatusClass = (status) => {
    switch(status?.toLowerCase()) {
      case 'submitted':
        return 'bg-success-subtle text-success';
      case 'in-progress':
        return 'bg-warning-subtle text-warning';
      case 'overdue-pending':
        return 'bg-danger-subtle text-danger';
      default:
        return 'bg-secondary-subtle text-secondary';
    }
  };

  const formatDate = (utcDateString) => {
    if (!utcDateString) return '';
    
    // Convert UTC to user's local timezone
    const utcDate = moment.utc(utcDateString);
    const localDate = utcDate.tz(userTimeZone);
    
    return `${localDate.format('MMMM D, YYYY h:mm A')} (${moment.tz(userTimeZone).zoneAbbr()})`;
  };

  const handleViewAssignment = (assignment) => {
    const encodedAssignmentId = encodeData(assignment.id);
    const encodedClassroomId = encodeData(classroom_id);
    navigate(`/user/assignmentsQuestions/${encodeURIComponent(encodedAssignmentId)}?classroomid=${encodeURIComponent(encodedClassroomId)}`, {
      state: { classroomName }
    });
  };

  return (
    <div className="overflow-hidden">
      <div className="flex flex-wrap items-center gap-3 px-0 py-0 border-b-0 bg-transparent">
        <div className="relative flex-1 min-w-[180px] max-w-xs">
          <Icon icon="fluent:search-20-regular" className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
          <input
            type="search"
            className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-gray-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#7083FF]/20 focus:border-[#7083FF]"
            placeholder="Search assignments..."
            value={search}
            onChange={handleSearch}
            aria-label="Search assignments"
          />
        </div>

        {search && (
          <button
            type="button"
            className="inline-flex items-center justify-center w-9 h-9 rounded-xl border border-gray-200 text-gray-600 hover:bg-gray-50"
            onClick={clearFilters}
            title="Clear search"
          >
            <Icon icon="mdi:close" width="18" height="18" />
          </button>
        )}
        <span className="text-sm text-gray-500 ml-auto">
          {totalRecords} assignment{totalRecords !== 1 ? 's' : ''}
        </span>
      </div>

      <div className="overflow-x-auto max-h-[70vh] overflow-y-auto mt-4">
        {loading ? (
          <div className="flex items-center justify-center min-h-[320px]">
            <div className="flex flex-col items-center gap-3 text-gray-500">
              <div className="animate-spin rounded-full h-10 w-10 border-2 border-gray-200 border-t-[#7083FF]" aria-hidden />
              <span className="text-sm font-medium">Loading assignments…</span>
            </div>
          </div>
        ) : (
          <table className="w-full text-sm" style={{ minWidth: '800px' }}>
            <thead className="bg-gray-50 sticky top-0 z-10 shadow-sm">
              <tr>
                <th className="px-4 py-3.5 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider w-16">#</th>
                <th className="px-4 py-3.5 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Title</th>
                <th className="px-4 py-3.5 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider w-44">Due date</th>
                <th className="px-4 py-3.5 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider w-28">Questions</th>
                <th className="px-4 py-3.5 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider w-28">Status</th>
                <th className="px-4 py-3.5 text-center text-xs font-semibold text-gray-600 uppercase tracking-wider w-28">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {assignments.length > 0 ? assignments.map((assignment, index) => (
                <tr key={assignment.id} className="hover:bg-gray-50 transition-colors">
                  <td className="px-4 py-3 align-middle text-gray-600">{((page - 1) * itemsPerPage) + index + 1}</td>
                  <td className="px-4 py-3 align-middle">
                    <div className="min-w-0">
                      <p className="font-medium text-gray-900 truncate m-0" title={assignment.title}>{assignment.title}</p>
                      {classroomName && <p className="text-xs text-gray-500 truncate m-0">{classroomName}</p>}
                    </div>
                  </td>
                  <td className="px-4 py-3 align-middle text-gray-600">
                    {assignment.due_date ? moment.utc(assignment.due_date).local().format('DD MMM YYYY, hh:mm A') : '—'}
                  </td>
                  <td className="px-4 py-3 align-middle text-gray-600">{assignment.total_questions || 0}</td>
                  <td className="px-4 py-3 align-middle">
                    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-semibold ${
                      assignment.status === "completed" ? 'bg-emerald-50 text-emerald-600' :
                      assignment.status === "pending" ? 'bg-amber-50 text-amber-600' :
                      assignment.status === "overdue-pending" ? 'bg-red-50 text-red-600' :
                      assignment.status === "overdue-completed" ? 'bg-emerald-50 text-emerald-600' : 'bg-gray-100 text-gray-600'
                    }`}>
                      <Icon icon={
                        assignment.status === "completed" ? 'mdi:check-circle' :
                        assignment.status === "pending" ? 'mdi:clock-alert' :
                        assignment.status === "overdue-pending" ? 'mdi:alert-circle' :
                        assignment.status === "overdue-completed" ? 'mdi:check-circle' : 'mdi:circle-outline'
                      } width="14" height="14" />
                      {assignment.status === "completed" ? 'Completed' :
                        assignment.status === "pending" ? 'Pending' :
                        assignment.status === "overdue-pending" ? 'Overdue' :
                        assignment.status === "overdue-completed" ? 'Completed late' : assignment.status}
                    </span>
                  </td>
                  <td className="px-4 py-3 align-middle">
                    <div className="flex items-center justify-center">
                      <button
                        type="button"
                        className="inline-flex items-center justify-center shrink-0 w-9 h-9 rounded-lg border border-[#7083FF] text-[#7083FF] hover:bg-[#7083FF]/10"
                        onClick={() => handleViewAssignment(assignment)}
                        title="View"
                      >
                        <Icon icon="mdi:eye" width="18" height="18" />
                      </button>
                    </div>
                  </td>
                </tr>
              )) : (
                <tr>
                  <td colSpan={6} className="px-4 py-16 text-center">
                    <div className="rounded-2xl border border-gray-100 bg-gray-50/50 py-16 px-6 text-center">
                      <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-gray-100 text-gray-400 mb-4">
                        <Icon icon="mdi:file-document-outline" width="36" height="36" />
                      </div>
                      <h3 className="text-lg font-semibold text-gray-700 mb-2">No assignments</h3>
                      <p className="text-gray-500 text-sm max-w-sm mx-auto">{search ? 'No assignments match your search.' : 'Assignments will appear here when your instructor adds them.'}</p>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        )}
      </div>

      {!loading && (
        <div className="mt-6 pt-4 border-t border-gray-100 flex flex-col sm:flex-row items-center justify-between gap-4 px-0 pb-0">
          <div className="flex items-center gap-3 text-sm text-gray-600">
            <span className="hidden sm:inline">Rows per page:</span>
            <select
              className="px-3 py-1.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#7083FF]/20 text-sm"
              value={itemsPerPage}
              onChange={handleLimitChange}
            >
              <option value={10}>10</option>
              <option value={25}>25</option>
              <option value={50}>50</option>
            </select>
            <span className="text-gray-500">
              {totalRecords === 0 ? 'No records' : `Showing ${(page - 1) * itemsPerPage + 1}–${Math.min(page * itemsPerPage, totalRecords)} of ${totalRecords}`}
            </span>
          </div>
          <div className="flex items-center gap-2 text-sm">
            <button
              type="button"
              className="inline-flex items-center justify-center w-9 h-9 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
              onClick={() => handlePageChange(page - 1)}
              disabled={page === 1 || totalPages === 0}
              title="Previous page"
            >
              <Icon icon="fluent:chevron-left-24-regular" width={16} height={16} />
            </button>
            <span className="px-2 text-gray-700">
              Page <span className="font-medium">{totalPages === 0 ? 0 : page}</span> of <span className="font-medium">{totalPages || 0}</span>
            </span>
            <button
              type="button"
              className="inline-flex items-center justify-center w-9 h-9 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
              onClick={() => handlePageChange(page + 1)}
              disabled={page === totalPages || totalPages === 0}
              title="Next page"
            >
              <Icon icon="fluent:chevron-right-24-regular" width={16} height={16} />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default Assignments;